import avatarGrameenphone from '../../../Icons/images/avatar-grameenphone.png';
import type {
  SidebarFooterConfig,
  SidebarFooterMenuItem,
  SidebarNotification,
  SidebarSection,
} from './Sidebar';

/**
 * The exact nav content of the Figma `Sidebar` frame (53:3371), used by the
 * stories so the rendered result can be diffed against the design.
 */
export const sidebarSections: SidebarSection[] = [
  {
    id: 'top',
    items: [
      { id: 'overview', label: 'Overview', icon: 'overview', href: '#overview' },
      { id: 'custom-reports', label: 'Custom reports', icon: 'customReport', href: '#custom-reports' },
      {
        id: 'deepdive-ai',
        label: 'DeepDive AI',
        icon: 'deepdiveAi',
        href: '#deepdive-ai',
        badge: { type: 'info', label: 'Beta' },
      },
      /*
       * Added 2026-09-21. The top group is four rows and 132 tall where it was
       * three and 102 — `_sidebar item` 2112:27357, `Icon Swap` resolving to the
       * `quick search` component (2113:29810), `Badge=False`.
       *
       * No `Beta` badge, which is worth stating because the reference
       * screenshots pinned to the `Quick Search` page show the row carrying one
       * in the product it was drawn from. The instance in *this* file sets
       * `Badge=False`, and the file is what is built.
       */
      { id: 'quick-search', label: 'Quick search', icon: 'quickSearch', href: '#quick-search' },
    ],
  },
  /*
   * `Monitor` is three rows and 102 tall as of 2026-09-17, down from four and
   * 132. `Crisis` — the row that moved up here from `Actions` in the 2026-09
   * update, relabelled from `Crisis alerts` and carrying a `Negative` badge of 3
   * — is **deleted**, not hidden: the group holds no such instance any more, the
   * same distinction drawn for `Narrative` below and against the hidden `Action`
   * group at the foot of this file. That badge was the only `negative` one in
   * the sidebar; nothing here draws one now.
   *
   * The first row has now been renamed twice without its icon ever moving:
   * `Social feed` → `Digital feed` (2026-09) → **`Mentions`** (2026-09-17). The
   * `Icon Swap` on that instance still resolves to the `social feed` component,
   * so the glyph is unchanged and only the word differs.
   *
   * `crisisAlerts` stays in `paths.ts` for the reason `narrative` does — a real
   * icon component with no row pointing at it is not a reason to narrow the
   * exported `IconName` union.
   */
  {
    id: 'monitor',
    label: 'Monitor',
    items: [
      { id: 'mentions', label: 'Mentions', icon: 'socialFeed', href: '#mentions' },
      { id: 'influencers', label: 'Influencers', icon: 'influencers', href: '#influencers' },
      { id: 'media', label: 'Media', icon: 'media', href: '#media' },
    ],
  },
  /*
   * `Analyze` was renamed `Intelligence` and gained `Narrative` and `Campaigns`.
   * Both are gone again, in two passes on 2026-09-17: `Narrative` first, then
   * `Campaigns`. Neither is hidden — the file holds no instance of either any
   * more, which is what separates a deletion from a `visible = false` row like
   * `Help centre` below or the whole `Action` group at the foot of this file.
   * The group is four rows and 132 tall, where a day earlier it was six and 192.
   *
   * `Audience` became **`Community`** in the same pass, keeping the `audience`
   * icon — `Icon Swap` still resolves to that component. That is the second pure
   * rename here after `Mentions` above: the word changes, the glyph does not.
   *
   * The `narrative` and `campaign` icons stay in `paths.ts`. They are real
   * components in the Figma icon set with no sidebar row pointing at them right
   * now, and dropping them would narrow the exported `IconName` union for no
   * gain.
   */
  {
    id: 'intelligence',
    label: 'Intelligence',
    items: [
      { id: 'brand-health', label: 'Brand health', icon: 'brandHealth', href: '#brand-health' },
      { id: 'community', label: 'Community', icon: 'audience', href: '#community' },
      { id: 'competitors', label: 'Competitors', icon: 'competitors', href: '#competitors' },
      { id: 'trends', label: 'Trends', icon: 'trends', href: '#trends' },
    ],
  },
];

/**
 * Figma's fourth group, `Action` — `Opportunities` (a `positive` badge of 5) over
 * `Reply agent`. The whole `Sidebar Item Wrapper` is `visible = false` as of
 * 2026-09-17, so it is not part of `sidebarSections` above, for the same reason
 * the hidden `Help centre` row is not part of `sidebarFooterItems`: the file
 * does not draw it, so neither does this.
 *
 * Kept as data rather than deleted, because hidden is not gone — the rows, their
 * icons and the badge all still exist in the file and the group reappears by
 * spreading this back into `sidebarSections`. `Narrative` above got the opposite
 * treatment and was deleted outright, because the file holds no instance of it
 * at all. That is the distinction worth preserving between the two.
 *
 * Note that `routes.ts` derives the route table from `sidebarSections`, so these
 * two ids currently have no route. Re-adding the group restores them.
 *
 * (The label landed misspelled as `Oppurtunities` and was corrected in the file
 * on 2026-09-13 — plan.md §7 item 181, now closed. `Opportunities` carries a
 * `Badge` rather than a `Counter`: the count sits in a rounded rectangle at
 * `Text xs/Regular` tinted `component/badge/success-surface`, which is `Badge`'s
 * `positive`. `srLabel` keeps the reading the `Counter`'s own `label` used to
 * give, since a bare "5" does not say what it counts.)
 */
export const sidebarActionSection: SidebarSection = {
  id: 'action',
  label: 'Action',
  items: [
    {
      id: 'opportunities',
      label: 'Opportunities',
      icon: 'ai',
      href: '#opportunities',
      badge: { type: 'positive', label: '5', srLabel: '5 new opportunities' },
    },
    { id: 'reply-agent', label: 'Reply agent', icon: 'replies', href: '#reply-agent' },
  ],
};

/**
 * The rows of Figma's `sidebar footer menu` (2130:51323), in order. Settings is
 * a link; `Invite member` opens the invite dialog, so the app supplies its
 * `onClick`; FAQ has no destination yet and is inert (the user's call,
 * 2026-09-21).
 *
 * `routes.ts` derives the Settings route from the linked rows here, so a row
 * with an `href` is also a route.
 */
export const sidebarFooterMenu: SidebarFooterMenuItem[] = [
  { id: 'settings', label: 'Settings', icon: 'settings', href: '#settings' },
  { id: 'invite-member', label: 'Invite member', icon: 'invite' },
  { id: 'faq', label: 'FAQ', icon: 'help' },
];

/**
 * Figma's own numbers: "2,137 left" over a bar at 80%, which puts the
 * allowance at 2,671. The app counts `left` down as DeepDive is used.
 */
export const sidebarCredits = { left: 2137, total: 2671 };

export const sidebarFooter: SidebarFooterConfig = {
  name: 'Grameenphone',
  avatarSrc: avatarGrameenphone,
  credits: sidebarCredits,
  profile: { name: 'John Doe', email: 'johndoe@gmail.com', initials: 'JD' },
  menu: sidebarFooterMenu,
  onSignOut: () => {},
};

/**
 * Figma `Sidebar Header`'s notification button (265:7545) — `State=Active` with
 * an `inbox` icon and the unread dot.
 */
export const sidebarNotification: SidebarNotification = {
  label: 'Inbox',
  icon: 'inbox',
  unread: true,
  unreadLabel: 'unread notifications',
};

export { avatarGrameenphone };
